import React, { useState, useEffect } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Shield, Lock, User } from 'lucide-react';
import api from '../services/api';

function SetupAdmin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');

  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.inviteEmail) {
          setTokenEmail(payload.inviteEmail);
        }
      } catch (e) {
        // token parsing failed, will be validated on server
      }
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/setup-admin', {
        token,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create account. The link may be expired or invalid.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px'
      }}>
        <div style={{
          background: 'rgba(255,255,255,0.95)',
          borderRadius: '20px',
          padding: '48px',
          maxWidth: '440px',
          width: '100%',
          textAlign: 'center'
        }}>
          <Shield size={48} style={{ color: '#ef4444', marginBottom: '16px' }} />
          <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>Invalid Link</h2>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>This invitation link is missing or invalid.</p>
          <Button variant="primary" onClick={() => navigate('/login')}>Go to Login</Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px'
      }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{
            background: 'rgba(255,255,255,0.95)',
            borderRadius: '20px',
            padding: '48px',
            maxWidth: '440px',
            width: '100%',
            textAlign: 'center'
          }}
        >
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #10b981, #059669)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', color: 'white', fontSize: '28px'
          }}>
            ✓
          </div>
          <h2 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '8px' }}>Account Created!</h2>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>
            Your admin account has been set up successfully. You can now log in.
          </p>
          <Button variant="primary" size="lg" onClick={() => navigate('/login')} style={{ width: '100%' }}>
            Go to Login
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      {/* Decorative orbs */}
      <div style={{
        position: 'fixed', top: '-120px', right: '-80px', width: '360px', height: '360px',
        borderRadius: '50%', background: 'rgba(255,255,255,0.08)', pointerEvents: 'none'
      }} />
      <div style={{
        position: 'fixed', bottom: '-100px', left: '-60px', width: '300px', height: '300px',
        borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none'
      }} />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          padding: '44px',
          maxWidth: '480px',
          width: '100%',
          boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
          border: '1px solid rgba(255,255,255,0.3)'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '16px',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', color: 'white', fontWeight: 800, fontSize: '22px'
          }}>
            R
          </div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, letterSpacing: '-0.5px', margin: '0 0 6px', color: '#1e293b' }}>
            Set Up Admin Account
          </h1>
          {tokenEmail && (
            <p style={{ color: '#6366f1', fontSize: '14px', fontWeight: 500, margin: 0 }}>
              {tokenEmail}
            </p>
          )}
        </div>

        {error && <Alert variant="danger" style={{ fontSize: '14px', borderRadius: '12px' }}>{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <Form.Group>
              <Form.Label style={{ fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <User size={13} /> First Name
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                placeholder="First name"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label style={{ fontSize: '13px', fontWeight: 500 }}>Last Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
                placeholder="Last name"
              />
            </Form.Group>
          </div>

          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Lock size={13} /> Password
            </Form.Label>
            <Form.Control
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              placeholder="Minimum 8 characters"
              minLength={8}
            />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label style={{ fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Lock size={13} /> Confirm Password
            </Form.Label>
            <Form.Control
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
              placeholder="Re-enter password"
            />
          </Form.Group>

          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              fontWeight: 600,
              fontSize: '15px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              border: 'none'
            }}
          >
            <Shield size={16} style={{ marginRight: '6px' }} />
            {loading ? 'Creating Account...' : 'Create Admin Account'}
          </Button>
        </Form>
      </motion.div>
    </div>
  );
}

export default SetupAdmin;
